"use client";

import { useState, useEffect, useRef, Fragment } from "react";
import Image from "next/image"
import MiniCart from "../MiniCart";
import { useMiniCart } from "@/app/context/MiniCartContext";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Transition } from "@headlessui/react";
import {
  Search,
  User as UserIcon,
  Heart,
  ShoppingBag,
  Menu as MenuIcon,
  ChevronDown,
  LogOut,
} from "lucide-react";
import AuthModal from "../AuthModal";

// Assuming these are imported from your contexts

// import { useAuth } from "@/context/AuthContext";

//--------------------------------IMPORTS END-------------------------------------------------


//------------------------------INTERFACES & TYPES START----------------------------------------------

interface NavLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  dropdown?: Array<{
    title: string;
    links: Array<{
      text: string;
      url: string;
    }>;
  }>;
}

interface Button {
  id: number;
  url: string;
  type: string;
  text: string;
  isLive: string;
  newTab: boolean;
}

interface CartResponse {
  id: number;
  attributes: {
    totalItems?: number;
    totalAmount?: number;
    currency?: string;
    lineItems?: {
      data: Array<{
        id: number;
        attributes: {
          quantity?: number;
        };
      }>;
    };
  };
}

// -------------------------------INTERFACES & TYPES END----------------------------------------------
export default function Navbar_Layout5({
  links,
  logoUrl,
  logoText,
  button,
  topBar2,
  cartData,
}: {
  links: Array<NavLink>;
  logoUrl: string | null;
  logoText: string | null;
  button: Array<Button>;
  topBar2: any;
  cartData: CartResponse;
}) {



// ------------------------------------------------STATES START-------------------------------------------------
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [showTopBanner, setShowTopBanner] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Auth states
  const [showAuth, setShowAuth] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userFirst, setUserFirst] = useState<string | null>(null);

  // Cart states
  const { open: openMiniCart } = useMiniCart();
  const [cartQtyCount, setCartQtyCount] = useState(0);
  const pathname = usePathname();
  const { wishlistCount = 0 } = { wishlistCount: 0 }; // Replace with useAuth()
  // Calculate cart quantity
  useEffect(() => {
    if (cartData?.attributes) {
      if (typeof cartData.attributes.totalItems === "number") {
        setCartQtyCount(cartData.attributes.totalItems);
      } else {
        const items = cartData.attributes.lineItems?.data || [];
        const sum = items.reduce(
          (acc, item) => acc + (item?.attributes?.quantity ?? 0),
          0,
        );
        setCartQtyCount(sum || items.length || 0);
      }
    }
  }, [cartData]);
  // Auth state management
  useEffect(() => {
    (async () => {
      try {
        const meRes = await fetch("/api/me", { cache: "no-store" });
        if (meRes.ok) {
          const me = await meRes.json();
          const logged = !!me?.isLoggedIn;
          setIsLoggedIn(logged);

          if (logged) {
            try {
              const uRes = await fetch("/api/user", { cache: "no-store" });
              if (uRes.ok) {
                const u = await uRes.json();
                const first =
                  u?.firstName ||
                  (u?.name ? String(u?.name).trim().split(" ")[0] : null);
                setUserFirst(first ?? null);
              } else {
                setUserFirst(null);
              }
            } catch {
              setUserFirst(null);
            }
          }
        }
      } catch {
        // ignore
      }
    })();
  }, []);
  // Handle scroll for smooth header transition
  useEffect(() => {
    let ticking = false;

    const updateHeader = () => {
      const currentScrollY = window.scrollY;

      // Show/hide top banner based on scroll
      if (currentScrollY > 50) {
        setShowTopBanner(false);
      } else {
        setShowTopBanner(true);
      }

      // Determine if scrolled for header style changes
      setIsScrolled(currentScrollY > 50);

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check
    updateHeader();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setShowPopover(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
   // HANDLE LOGOUT
  const handleLogout = async () => {
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore network error
    } finally {
      setIsLoggedIn(false);
      setUserFirst(null);
      window.location.href = "/";
    }
  };
// HANDLE AUTHCLICK
  const handleAuthClick = () => {
    setShowAuth(true);
  };
// HANDLE AUTHCLICK
  const handleLoginSuccess = async () => {
    setShowAuth(false);
    setIsLoggedIn(true);
    try {
      const uRes = await fetch("/api/user", { cache: "no-store" });
      if (uRes.ok) {
        const u = await uRes.json();
        const first =
          u?.firstName ||
          (u?.name ? String(u?.name).trim().split(" ")[0] : null);
        setUserFirst(first ?? null);

        const isFirstOrder = !u?.orders || u?.orders?.length === 0;
        localStorage.setItem("isFirstOrder", JSON.stringify(isFirstOrder));
      }
    } catch {
      // ignore
    }
  };
// ------------------------------------------------STATES END-------------------------------------------------
  return (
    <>
      <header
        className={`${isScrolled ? "fixed" : "sticky"} top-0 left-0 w-full z-50 transition-all duration-500 ease-out`}
        style={{
          transform: isScrolled ? "translateY(0)" : "translateY(0)",
        }}
      >
        {/* ✅ CONDITIONAL TOP BANNER with smooth transition */}

        {/* MAIN NAVIGATION with smooth background transition */}
        <div
          className={`bg-white transition-all duration-500 ease-out ${
            isScrolled
              ? "shadow-lg shadow-black/5 border-b border-neutral-100"
              : "border-b border-transparent"
          }`}
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between h-16 transition-all duration-300">
              {/* Mobile Menu Button */}
              <div className="flex items-center lg:hidden">
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="p-2 text-neutral-900 focus:outline-none hover:opacity-70 transition-opacity"
                  aria-label="Open menu"
                >
                  <MenuIcon size={24} strokeWidth={1.5} />
                </button>
              </div>

              {/* Logo - Uses the same logoUrl */}
              <div className="flex-shrink-0 flex items-center">
                {logoUrl ? (
                  <Link href="/" className="group">
                    <Image
                      src={logoUrl}
                      alt={logoText || "Brand Logo"}
                      width={220}
                      height={40}
                      priority
                      className="h-auto w-full object-contain transition-all duration-500 ease-out group-hover:opacity-80"
                      sizes="(max-width: 768px) 120px, (max-width: 1024px) 140px, 160px"
                    />
                  </Link>
                ) : (
                  <Link href="/" className="group">
                    <p className="text-xl md:text-2xl font-black uppercase tracking-[0.1em] text-neutral-900 transition-all duration-300">
                      {logoText?.split(" ")[0] || "Little"}
                      <span
                        className="italic ml-0.5 transition-all duration-300"
                        style={{
                          color: isScrolled
                            ? "var(--secondary)"
                            : "var(--primary)",
                        }}
                      >
                        {logoText?.split(" ")[1] || "Ginnie"}
                      </span>
                    </p>
                  </Link>
                )}
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-8 h-full">
                {links?.map((link) => {
                  const isActive = pathname === link.url;
                  const hasDropdown = !!link.dropdown?.length;
                  return (
                    <div
                      key={link.id}
                      className="relative h-full flex items-center"
                      onMouseEnter={() =>
                        hasDropdown && setActiveDropdown(link.id)
                      }
                      onMouseLeave={() => setActiveDropdown(null)}
                    >
                      <Link
                        href={link.url || "#"}
                        className={`text-[11px] font-bold uppercase tracking-[0.2em] transition-all duration-300 relative pb-1 flex items-center gap-1 ${
                          isActive
                            ? "text-neutral-900"
                            : "text-neutral-600 hover:text-black"
                        }`}
                      >
                        {link.text}
                        {hasDropdown && (
                          <ChevronDown
                            size={12}
                            className={`transition-transform duration-300 ${
                              activeDropdown === link.id ? "rotate-180" : ""
                            }`}
                          />
                        )}
                        <span
                          className={`absolute bottom-0 left-0 h-[1.5px] transition-all duration-500 ease-out ${
                            isActive || activeDropdown === link.id
                              ? "w-full opacity-100"
                              : "w-0 opacity-0 group-hover:w-full group-hover:opacity-100"
                          }`}
                          style={{
                            backgroundColor: isActive
                              ? "var(--primary)"
                              : "var(--secondary)",
                          }}
                        ></span>
                      </Link>

                      {hasDropdown && (
                        <Transition
                          show={activeDropdown === link.id}
                          as={Fragment}
                          enter="transition-all duration-300 ease-out"
                          enterFrom="opacity-0 translate-y-4"
                          enterTo="opacity-100 translate-y-0"
                          leave="transition-all duration-200 ease-in"
                          leaveFrom="opacity-100 translate-y-0"
                          leaveTo="opacity-0 translate-y-4"
                        >
                          <div className="absolute top-full left-1/2 -translate-x-1/2 w-[500px] bg-white shadow-2xl rounded-b-xl border border-neutral-50 p-8 grid grid-cols-2 gap-8 z-50">
                            {link.dropdown?.map((category, idx) => (
                              <div key={idx} className="space-y-4">
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-700 border-b border-neutral-50 pb-2">
                                  {category.title}
                                </h4>
                                <ul className="space-y-2">
                                  {category.links.map((sub, sIdx) => (
                                    <li key={sIdx}>
                                      <Link
                                        href={sub.url}
                                        className="text-sm text-neutral-600 hover:text-[var(--secondary)] transition-colors duration-300 block py-1"
                                        onClick={() => setActiveDropdown(null)}
                                      >
                                        {sub.text}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </Transition>
                      )}
                    </div>
                  );
                })}
              </nav>

              {/* Right Side Icons */}
              <div className="flex items-center space-x-2 md:space-x-4">
                {/* Search Bar - Smooth width transition */}
                <div
                  className={`hidden lg:flex items-center border-b transition-all duration-500 ease-out py-1 focus-within:border-neutral-900 ${
                    isScrolled
                      ? "border-neutral-100 w-32"
                      : "border-neutral-200 w-40"
                  }`}
                >
                  <Search
                    size={18}
                    className="text-gray-700 transition-colors duration-300"
                    strokeWidth={2}
                  />
                  <form action="/search" method="GET" className="flex items-center">

  <input
    type="text"
    name="q"
    placeholder="SEARCH"
    className={`bg-transparent border-none focus:ring-0 text-[10px] font-bold tracking-widest ml-2 placeholder:text-gray-700 transition-all duration-500 ${
      isScrolled ? "w-24" : "w-32"
    }`}
  />
</form>
                </div>

                {/* User Icon with Popover */}
                <div className="relative">
                  <button
                    onClick={() =>
                      isLoggedIn
                        ? setShowPopover(!showPopover)
                        : setShowAuth(true)
                    }
                    className="p-2 transition-all duration-300 hover:scale-110"
                    aria-label={isLoggedIn ? "Account menu" : "Login"}
                  >
                    <UserIcon
                      size={20}
                      strokeWidth={1.5}
                      style={{
                        color: isLoggedIn
                          ? "var(--secondary)"
                          : "var(--text-2)",
                      }}
                    />
                  </button>
                  {showPopover && (
                    <Transition
                      show={showPopover}
                      as={Fragment}
                      enter="transition-all duration-200 ease-out"
                      enterFrom="opacity-0 scale-95"
                      enterTo="opacity-100 scale-100"
                      leave="transition-all duration-150 ease-in"
                      leaveFrom="opacity-100 scale-100"
                      leaveTo="opacity-0 scale-95"
                    >
                      <div
                        ref={popoverRef}
                        className="absolute right-0 mt-3 w-44 bg-white border border-neutral-100 shadow-xl rounded-md overflow-hidden z-50"
                      >
                        <Link
                          href="/account"
                          className="flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest hover:bg-neutral-50 transition-colors duration-200"
                          onClick={() => setShowPopover(false)}
                        >
                          <UserIcon size={14} /> Account
                        </Link>
                        <button
                          onClick={() => {
                            handleLogout();
                            setShowPopover(false);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest text-red-600 hover:bg-neutral-50 transition-colors duration-200"
                        >
                          <LogOut size={14} /> Logout
                        </button>
                      </div>
                    </Transition>
                  )}
                </div>

                {/* Wishlist Icon */}
                <button
                  className="p-2 text-neutral-700 hover:text-black relative group hidden sm:block transition-all duration-300 hover:scale-110"
                  aria-label="Wishlist"
                  onClick={() => {
                    if (isLoggedIn) {
                      // Navigate to wishlist page
                      window.location.href = "/account?tab=my-wishlist";
                    } else {
                      setShowAuth(true);
                    }
                  }}
                >
                  <Heart size={20} strokeWidth={1.5} />
                  {(wishlistCount ?? 0) > 0 && (
                    <span
                      className="absolute top-1 right-0.5 h-4 w-4 flex items-center justify-center rounded-full text-[9px] font-bold shadow-sm border-2 border-white"
                      style={{
                        backgroundColor: "var(--secondary)",
                        color: "white",
                      }}
                    >
                      {wishlistCount}
                    </span>
                  )}
                  <span
                    className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full transition-all duration-300 group-hover:opacity-100 group-hover:scale-150"
                    style={{
                      backgroundColor: "var(--secondary)",
                      opacity: isScrolled ? 0.7 : 0,
                    }}
                  ></span>
                </button>

                {/* Cart Icon */}
                <button
                  onClick={() => openMiniCart()}
                  className="p-2 text-neutral-700 hover:text-black relative group transition-all duration-300 hover:scale-110"
                  aria-label="Shopping cart"
                >
                  <ShoppingBag
                    size={22}
                    strokeWidth={1.5}
                    className="transition-all duration-300"
                  />
                  {cartQtyCount > 0 && (
                    <Transition
                      show={cartQtyCount > 0}
                      enter="transition-all duration-300 ease-out"
                      enterFrom="scale-0 opacity-0"
                      enterTo="scale-100 opacity-100"
                      leave="transition-all duration-200 ease-in"
                      leaveFrom="scale-100 opacity-100"
                      leaveTo="scale-0 opacity-0"
                    >
                      <span
                        className="absolute top-1 right-0.5 h-4 w-4 flex items-center justify-center rounded-full text-[9px] font-bold shadow-sm border-2 border-white"
                        style={{
                          backgroundColor: "var(--secondary)",
                          color: "white",
                        }}
                      >
                        {cartQtyCount}
                      </span>
                    </Transition>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Add your mobile menu component here */}
      {/* <MobileMenu 
        isOpen={mobileMenuOpen} 
        onClose={() => setMobileMenuOpen(false)} 
        links={links}
      /> */}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
}
